/**
 * Shared GraphQL POST helper used by the live client and the offline flush.
 */
import axios from "axios";

/** Mutation document that matches `module_inspect_edit(input: [ModuleInspectEditInput])`. */
export const MODULE_INSPECT_MUTATION = `mutation module_inspect_edit($input: [ModuleInspectEditInput]) {
  module_inspect_edit(input: $input)
}`;

/** Mutation document that flushes queued chat messages in one request. */
export const CHAT_ADD_BATCH_MUTATION = `mutation chat_add_batch($messages: [ChatAddInput]) {
  chat_add_batch(messages: $messages)
}`;

/**
 * Reads GraphQL error messages from an Axios response body.
 *
 * @param {Object} res Axios response.
 * @returns {string}
 */
export const gqlErrorMessage = (res) => {
  const errors = res?.data?.errors;
  if (!Array.isArray(errors) || !errors.length) return "";
  return errors.map((err) => err && err.message).filter(Boolean).join("; ") || "GraphQL error";
};

/**
 * Unwraps a single-field GraphQL response payload.
 *
 * @param {Object} res Axios response.
 * @returns {*} Field value from `data.data`.
 */
export const unwrapGql = (res) => {
  const message = gqlErrorMessage(res);
  if (message) throw Error(message);

  const payload = res?.data?.data;
  if (!payload || typeof payload !== "object") throw Error("There is error in return value");
  const keys = Object.keys(payload);
  if (keys.length !== 1) throw Error("There is error in return value");
  const value = payload[keys[0]];
  if (value == null) throw Error("GQL returned null value");
  return value;
};

/**
 * Posts a GraphQL document with the stored JWT.
 *
 * @param {string} query GraphQL document.
 * @param {Object} [variables={}] Variables.
 * @returns {Promise<*>} Unwrapped field value.
 */
export const postGql = async (query, variables = {}) => {
  const jwt = localStorage.getItem("jwt-ghost") || localStorage.getItem("jwt");
  try {
    const res = await axios.post(
      process.env.REACT_APP_GQL_ENDPOINT,
      JSON.stringify({
        query,
        variables,
      }),
      { headers: { Authorization: jwt, "Content-Type": "application/json" } }
    );
    return unwrapGql(res);
  } catch (err) {
    if (err?.response?.data) unwrapGql(err.response);
    throw err;
  }
};

/**
 * Fire-and-forget GraphQL POST that still runs during simulated offline and
 * page unload (`keepalive`), used for presence leave.
 *
 * @param {string} query GraphQL document.
 * @param {Object} [variables={}] Variables.
 * @returns {Promise<void>}
 */
export const postGqlKeepalive = async (query, variables = {}) => {
  if (typeof fetch === "undefined") {
    await postGql(query, variables);
    return;
  }
  const jwt = localStorage.getItem("jwt-ghost") || localStorage.getItem("jwt");
  const endpoint = process.env.REACT_APP_GQL_ENDPOINT;
  if (!endpoint) return;
  await fetch(endpoint, {
    method: "POST",
    headers: { Authorization: jwt || "", "Content-Type": "application/json" },
    body: JSON.stringify({ query, variables }),
    keepalive: true,
    cache: "no-store",
  });
};

/**
 * Persist every queued chat message in one mutation.
 *
 * @param {Array<Object>} messages ChatAddInput objects.
 * @returns {Promise<true>}
 */
export const postChatAddBatch = async (messages) => {
  const items = (messages || []).filter((item) => item && typeof item === "object" && !Array.isArray(item));
  if (!items.length) return true;
  await postGql(CHAT_ADD_BATCH_MUTATION, { messages: items });
  return true;
};

/**
 * Posts every coalesced module-inspect payload in one list-input mutation.
 *
 * @param {Array<Object>} inputs Combined ModuleInspectEditInput objects.
 * @returns {Promise<true>}
 */
export const postModuleInspectBatch = async (inputs) => {
  const items = (inputs || []).filter((item) => item && typeof item === "object" && !Array.isArray(item));
  if (!items.length) return true;
  await postGql(MODULE_INSPECT_MUTATION, { input: items });
  return true;
};
